import { Bell, LogOut, Menu, Search, Settings, User as UserIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { ROLE_LABELS } from '@/entities/user/types';
import { selectCurrentUser, useLogout } from '@/features/auth';
import { mobileSidebarOpened } from '@/features/ui';
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ThemeToggle,
  toast,
  Tooltip,
  TooltipProvider,
} from '@/shared/ui';

import { Breadcrumbs } from './Breadcrumbs';

export function Topbar(): ReactElement {
  const dispatch = useAppDispatch();

  return (
    <TooltipProvider delayDuration={250}>
      <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-md md:gap-4 md:px-6">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Abrir menú"
          onClick={() => {
            dispatch(mobileSidebarOpened());
          }}
          className="md:hidden"
        >
          <Menu />
        </Button>

        <Breadcrumbs />

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className="hidden h-9 items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 text-xs text-muted-foreground transition-colors hover:bg-muted lg:flex"
            aria-label="Buscar (Ctrl+K)"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Buscar afiliados, contratos…</span>
            <kbd className="ml-6 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] font-semibold text-foreground/70">
              Ctrl K
            </kbd>
          </button>

          <Tooltip content="Notificaciones">
            <Button variant="ghost" size="icon-sm" aria-label="Notificaciones" className="relative">
              <Bell className="h-4 w-4" />
              <span
                className="absolute right-1.5 top-1.5 grid h-3.5 w-3.5 place-items-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground"
                aria-label="Tienes notificaciones nuevas"
              >
                3
              </span>
            </Button>
          </Tooltip>

          <ThemeToggle />

          <UserMenu />
        </div>
      </header>
    </TooltipProvider>
  );
}

function UserMenu(): ReactElement {
  const user = useAppSelector(selectCurrentUser);
  const logoutMutation = useLogout();

  const fullName = user?.fullName ?? 'Usuario';
  const role = user?.role ?? 'USER';
  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

  const handleLogout = (): void => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Sesión cerrada');
      },
      onSettled: () => {
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
      },
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Menú de usuario"
          className="flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback>{initials || 'U'}</AvatarFallback>
          </Avatar>
          <div className="hidden text-left lg:block">
            <div className="max-w-[140px] truncate text-xs font-semibold leading-none text-foreground">
              {fullName}
            </div>
            <div className="mt-0.5 text-[10px] leading-none text-muted-foreground">
              {ROLE_LABELS[role]}
            </div>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Mi cuenta</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/perfil">
            <UserIcon /> Perfil
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/cambiar-clave">
            <Settings /> Cambiar contraseña
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          disabled={logoutMutation.isPending}
          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <LogOut /> Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
